import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as ArrowLeftIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { applyTypography, BaseButton, useT } from '@repo/common';
import { getPathFeedSlides } from '../../../api/workshop/pathFeedSlide.api';
import HeroSlideManager from './HeroSlideManager';

const ArrowButton = ({ direction = 'left', onClick, disabled }) => {
  const t = useT();
  const isLeft = direction === 'left';

  return (
    <BaseButton
      type='text'
      size='s'
      aria-label={
        isLeft
          ? t('previous highlight', '이전 하이라이트')
          : t('next highlight', '다음 하이라이트')
      }
      onClick={onClick}
      isDisabled={disabled}
      icon={isLeft ? <ArrowLeftIcon /> : <ArrowRightIcon />}
      bgclass='bg-grayscale-gray02 disabled:opacity-40 h-9 w-9 min-w-0 p-0 rounded-[50px]'
      borderclass='border-0'
      textclass='text-icon-primary'
      hoverclass='hover:opacity-80 hover:bg-grayscale-gray02 hover:text-icon-primary'
      activeclass='active:opacity-80 active:bg-grayscale-gray02 active:text-icon-primary'
    />
  );
};

const HERO_IMAGE = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-pathfeed-hero.svg`;

const DEFAULT_SLIDES = [
  {
    image: HERO_IMAGE,
    title: 'Discover. Connect. Express — your first step into real Korean life',
    description:
      'description description description description description description description description description description description description description description description description description description',
  },
  {
    image: HERO_IMAGE,
    title: 'Second Slide Title',
    description:
      'Second slide description description description description description description description description description description description description',
  },
  {
    image: HERO_IMAGE,
    title: 'Third Slide Title',
    description:
      'Third slide description description description description description description description description description description description description',
  },
  {
    image: HERO_IMAGE,
    title: 'Fourth Slide Title',
    description:
      'Fourth slide description description description description description description description description description description description description',
  },
];

const EMPTY_STATE_SLIDES = [
  {
    image: HERO_IMAGE,
    title: 'New PathFeed experiences are coming soon',
    description: 'Please check back for the next Koready highlight.',
  },
];

const HeroSection = ({ isAdmin = false }) => {
  const t = useT();
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobileDescriptionOpen, setIsMobileDescriptionOpen] = useState(false);
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const renderedSlides = slides.length ? slides : EMPTY_STATE_SLIDES;
  const totalSlides = renderedSlides.length;

  const refreshSlides = useCallback(async () => {
    try {
      const data = await getPathFeedSlides();
      setSlides(
        data.map((slide) => ({
          ...slide,
          image: slide.imageUrl || HERO_IMAGE,
        })),
      );
    } catch (error) {
      console.error('Failed to load PathFeed slides:', error);
      setSlides(DEFAULT_SLIDES);
    }
  }, []);

  useEffect(() => {
    refreshSlides();
  }, [refreshSlides]);

  useEffect(() => {
    setCurrentSlide((previous) =>
      Math.min(previous, Math.max(slides.length - 1, 0)),
    );
  }, [slides.length]);

  const goToPrevious = () => {
    setIsMobileDescriptionOpen(false);
    setCurrentSlide((previous) =>
      previous === 0 ? totalSlides - 1 : previous - 1,
    );
  };

  const goToNext = () => {
    setIsMobileDescriptionOpen(false);
    setCurrentSlide((previous) =>
      previous === totalSlides - 1 ? 0 : previous + 1,
    );
  };

  const goToSlide = (index) => {
    setIsMobileDescriptionOpen(false);
    setCurrentSlide(index);
  };

  const onTouchStart = (event) => {
    setTouchEnd(null);
    setTouchStart(event.targetTouches[0].clientX);
  };

  const onTouchMove = (event) => {
    setTouchEnd(event.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;

    const distance = touchStart - touchEnd;
    if (distance > 50) goToNext();
    if (distance < -50) goToPrevious();
  };

  return (
    <section className='relative flex flex-col px-0 pb-[30px] pt-[30px] tabletH:px-10 tabletH:pb-[60px] tabletV:pt-10 desktopL:px-20 desktopL:pb-20'>
      {isAdmin && (
        <BaseButton
          type='text'
          size='m'
          hideIcon
          typo={false}
          onClick={() => setIsManagerOpen(true)}
          className='absolute right-4 top-3 z-20 h-auto min-w-0 rounded-lg border border-green-deep02 bg-white px-4 py-2 typo-b14Strong text-green-deep02 shadow-sm hover:bg-green-light01 hover:text-green-deep02 tabletH:right-10 tabletH:top-[30px] tabletV:top-10 desktopL:right-20'
        >
          {t('Manage slides', '슬라이드 관리')}
        </BaseButton>
      )}
      <div className='relative flex w-full flex-col items-center'>
        <div
          className='relative flex h-[500px] w-full flex-col items-center justify-end overflow-hidden tabletH:gap-6 tabletH:rounded-[50px] tabletH:px-[50px] desktopL:hidden'
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-0'
          >
            <div className='absolute inset-0 bg-grayscale-gray00' />
            <div className='absolute inset-0 overflow-hidden tabletH:rounded-[50px]'>
              <div
                className='flex h-full transition-transform duration-500 ease-in-out'
                style={{
                  transform: `translateX(-${
                    currentSlide * (100 / totalSlides)
                  }%)`,
                  width: `${totalSlides * 100}%`,
                }}
              >
                {renderedSlides.map((slide, index) => (
                  <img
                    key={index}
                    src={slide.image}
                    alt={`${slide.title} preview`}
                    className='h-full max-w-none flex-shrink-0 object-cover object-center'
                    style={{ width: `${100 / totalSlides}%` }}
                  />
                ))}
              </div>
            </div>
            <div className='absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.6)] to-[70%] tabletH:rounded-[50px]' />
          </div>

          <div className='relative z-10 flex w-full flex-col items-start px-6 pb-6 tabletH:px-0 tabletH:pb-0'>
            <p
              className={`${applyTypography(
                'title1',
              )} w-full whitespace-pre-wrap leading-[1.2] text-text-inverse tabletH:typo-h7`}
            >
              {renderedSlides[currentSlide].title}
              {!isMobileDescriptionOpen ? (
                <>
                  {' '}
                  <BaseButton
                    type='text'
                    size='s'
                    hideIcon
                    typo={false}
                    isStatic
                    className={`${applyTypography(
                      'b14',
                    )} m-0 h-auto min-w-0 cursor-pointer border-0 bg-transparent p-0 align-baseline text-text-inverse underline decoration-[7%]`}
                    textWrapperClass='h-auto'
                    onClick={() => setIsMobileDescriptionOpen(true)}
                    aria-label={t('Show description', '설명 보기')}
                  >
                    {t('more', '더보기')}
                  </BaseButton>
                </>
              ) : (
                <>
                  {' '}
                  <span
                    className={`${applyTypography('b14')} text-text-inverse`}
                  >
                    {renderedSlides[currentSlide].description}
                  </span>{' '}
                  <BaseButton
                    type='text'
                    size='s'
                    hideIcon
                    typo={false}
                    isStatic
                    className={`${applyTypography(
                      'b14',
                    )} m-0 h-auto min-w-0 cursor-pointer border-0 bg-transparent p-0 align-baseline text-text-inverse underline decoration-[7%]`}
                    textWrapperClass='h-auto'
                    onClick={() => setIsMobileDescriptionOpen(false)}
                    aria-label={t('Hide description', '설명 닫기')}
                  >
                    {t('close', '닫기')}
                  </BaseButton>
                </>
              )}
            </p>
          </div>

          <div className='relative flex w-full items-center justify-center pb-4'>
            {renderedSlides.map((_, index) => (
              <BaseButton
                key={index}
                type='text'
                size='s'
                isStatic
                onClick={() => goToSlide(index)}
                aria-label={t(
                  `Go to slide ${index + 1}`,
                  `${index + 1}번 슬라이드로 이동`,
                )}
                className={`h-auto min-w-0 cursor-pointer border-0 bg-transparent px-[6px] py-2 ${
                  index === currentSlide ? 'w-9 [&>span]:flex-col' : ''
                }`}
                icon={
                  <span
                    className={`block rounded-xl ${
                      index === currentSlide
                        ? 'h-2 w-full bg-green-deep01'
                        : 'size-2 bg-grayscale-gray03'
                    }`}
                  />
                }
              />
            ))}
          </div>
        </div>

        <div className='relative hidden h-[520px] w-full overflow-hidden rounded-[50px] bg-grayscale-gray00 desktopL:block'>
          <div
            className='flex h-full w-full transition-transform duration-500 ease-in-out'
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {renderedSlides.map((slide, index) => (
              <div key={index} className='flex h-full min-w-full flex-none'>
                <div className='relative flex-1'>
                  <img
                    src={slide.image}
                    alt={`${slide.title} preview`}
                    className='pointer-events-none absolute inset-0 h-full w-full object-cover object-center'
                  />
                </div>
                <div className='flex min-w-0 flex-1 flex-col items-start justify-center gap-[18px] px-20'>
                  <h2
                    className={`${applyTypography(
                      'h6',
                    )} leading-[1.2] text-text-primary`}
                  >
                    {slide.title}
                  </h2>
                  <p
                    className={`${applyTypography(
                      'b18Strong',
                    )} w-full max-w-[596px] leading-[1.5] text-grayscale-gray07`}
                  >
                    {slide.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='hidden items-center justify-center gap-4 pt-5 desktopL:flex'>
          <ArrowButton
            direction='left'
            onClick={goToPrevious}
            disabled={totalSlides <= 1}
          />
          <ArrowButton
            direction='right'
            onClick={goToNext}
            disabled={totalSlides <= 1}
          />
        </div>
      </div>

      {isManagerOpen && (
        <HeroSlideManager
          fallbackImage={HERO_IMAGE}
          onClose={() => setIsManagerOpen(false)}
          onChanged={refreshSlides}
        />
      )}
    </section>
  );
};

export default HeroSection;
